import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Pricing = () => {
  // Centralized pricing variables
  const monthlySubscriptionPrice = 20; // $20/month when paying monthly
  const annualSubscriptionPrice = 17; // $17/month when paying annually (25% discount)
 
  // Calculate annual discount
  const annualDiscount = ((monthlySubscriptionPrice - annualSubscriptionPrice) / monthlySubscriptionPrice) * 100;
  
  // Included Free Respondents tier respondents
  const freeRespondentsPerMonth = 20; // Free tier respondents
  const proRespondentsPerMonth = 200; // Professional tier included respondents

 
  // Pay-as-you-go pricing
  const perRespondent = 0.05; // $0.05 per AI respondent
  const humanPerRespondent = 2.00; // $2.00 per human respondent (fixed)
  
  // State variables
  const [respondentsCount, setRespondentsCount] = useState(1000);
  const [totalAiPrice, setTotalAiPrice] = useState(0);
  const [isAnnual, setIsAnnual] = useState(true);
  
  // Calculate annual savings
  const annualSavings = (monthlySubscriptionPrice - annualSubscriptionPrice) * 12;
  
  useEffect(() => {
    // Calculate total AI price based on respondent count
    setTotalAiPrice(respondentsCount * perRespondent);
  }, [respondentsCount]);

  // Google Analytics tracking functions
  const waitForGtag = (callback, maxRetries = 10, currentRetry = 0) => {
    if (typeof window !== 'undefined' && window.gtag) {
      callback();
    } else if (currentRetry < maxRetries) {
      console.log(`⏳ Waiting for gtag to load... (attempt ${currentRetry + 1}/${maxRetries})`);
      setTimeout(() => waitForGtag(callback, maxRetries, currentRetry + 1), 200);
    } else {
      console.error('❌ gtag failed to load after maximum retries');
    }
  };

  const trackButtonClick = (buttonName, planType, value = null) => {
    console.log(`🎯 Button clicked: ${buttonName} - ${planType}`);
    
    waitForGtag(() => {
      console.log(`✅ gtag loaded - tracking ${buttonName} click for ${planType}`);
      
      try {
        // Track the button click event
        window.gtag('event', 'button_click', {
          event_category: 'pricing',
          event_label: buttonName,
          plan_type: planType,
          billing_cycle: isAnnual ? 'annual' : 'monthly',
          value: value || 0,
          currency: 'USD'
        });

        console.log(`✅ button_click event sent:`, {
          event_category: 'pricing',
          event_label: buttonName,
          plan_type: planType,
          billing_cycle: isAnnual ? 'annual' : 'monthly',
          value: value || 0
        });

        // Track conversion events for key actions
        if (buttonName === 'Start Free' || buttonName === 'Get Started') {
          window.gtag('event', 'conversion', {
            event_category: 'pricing',
            event_label: `${buttonName} - ${planType}`,
            plan_type: planType,
            billing_cycle: isAnnual ? 'annual' : 'monthly',
            value: value || 0,
            currency: 'USD'
          });

          console.log(`✅ conversion event sent:`, {
            event_category: 'pricing',
            event_label: `${buttonName} - ${planType}`,
            plan_type: planType,
            billing_cycle: isAnnual ? 'annual' : 'monthly',
            value: value || 0
          });
        }
      } catch (error) {
        console.error('❌ Error sending GA4 event:', error);
      }
    });
  };

  const handleSliderChange = (e) => {
    setRespondentsCount(parseInt(e.target.value));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDollars = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const calculateSliderPercentage = (value, min, max) => {
    return ((value - min) / (max - min)) * 100;
  };
  
  // Calculate human respondent pricing (for comparison)
  const humanTotalPrice = respondentsCount * humanPerRespondent;
  const savings = humanTotalPrice - totalAiPrice;
  const savingsPercentage = Math.round((savings / humanTotalPrice) * 100);

  // Current subscription price based on billing cycle
  const currentSubscriptionPrice = isAnnual ? annualSubscriptionPrice : monthlySubscriptionPrice;

  return (
    <section className="py-20 bg-black">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent">
              Pricing
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Get access to our AI-powered survey platform at an affordable price
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center items-center mb-12">
          <div className="relative inline-flex rounded-full bg-gray-900/70 p-1">
            <button
              className={`relative z-10 rounded-full py-2 px-6 font-medium transition-all duration-200 ${
                !isAnnual ? 'text-white' : 'text-gray-400'
              }`}
              onClick={() => setIsAnnual(false)}
            >
              Monthly
            </button>
            <button
              className={`relative z-10 rounded-full py-2 px-6 font-medium transition-all duration-200 ${
                isAnnual ? 'text-white' : 'text-gray-400'
              }`}
              onClick={() => setIsAnnual(true)}
            >
              Annually
            </button>
            <div 
              className={`absolute inset-0 z-0 w-1/2 h-full bg-gradient-to-r from-pink to-blue rounded-full transition-transform duration-300 ease-in-out ${
                isAnnual ? 'translate-x-full' : ''
              }`} 
            />
            {isAnnual && (
              <div className="absolute -top-8 right-4 flex items-center text-green-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">Save {annualDiscount}%</span>
              </div>
            )}
          </div>
        </div>

        {/* Section 1: Subscription Plans (2/3 of screen) */}
        <div className="mb-32">
          <h2 className="text-3xl font-bold text-center mb-12">
            <span className="bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent">
              Pick Your Subscription
            </span>
          </h2>

          {/* Pricing Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Free Tier */}
            <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden hover:border-pink/50 transition duration-300">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-white">Free</h3>
                  <span className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full">STARTER</span>
                </div>
                <div className="flex items-baseline mb-4">
                  <span className="text-5xl font-bold bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent">$0</span>
                  <span className="text-gray-400 ml-2">/month</span>
                </div>
                <ul className="text-gray-300 mb-8 space-y-3">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {freeRespondentsPerMonth} AI respondents per month
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Basic survey creation tools
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Email support
                  </li>
                  <li className="flex items-center text-gray-500">
                    <svg className="w-5 h-5 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Message testing capability
                  </li>
                  <li className="flex items-center text-gray-500">
                    <svg className="w-5 h-5 text-gray-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Advanced post processing
                  </li>
                </ul>
                <button 
                  className="w-full border border-pink/70 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:bg-pink/20"
                  onClick={() => trackButtonClick('Start Free', 'Free', 0)}
                >
                  Start Free
                </button>
              </div>
            </div>

            {/* Professional Tier */}
            <div className="bg-black/40 backdrop-blur-sm border-2 border-pink/80 rounded-xl overflow-hidden transform scale-105 z-10 relative">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-pink to-blue text-white text-xs py-1 px-3 rounded-bl-lg">
                POPULAR
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-white">Professional</h3>
                  <span className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded-full">RECOMMENDED</span>
                </div>
                <div className="flex items-baseline mb-4">
                  <span className="text-5xl font-bold bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent">${currentSubscriptionPrice}</span>
                  <span className="text-gray-400 ml-2">/month</span>
                </div>
                {isAnnual && (
                  <p className="text-sm text-green-500 mb-4">
                    Save ${annualSavings} annually
                  </p>
                )}
                <ul className="text-gray-300 mb-8 space-y-3">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Includes {proRespondentsPerMonth} AI respondents per month
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Advanced survey creation tools
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Priority support
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Message testing capability
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Advanced post processing
                  </li>
                </ul>
                <button 
                  className="w-full bg-gradient-to-r from-pink to-blue text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:opacity-90 btn-hover"
                  onClick={() => trackButtonClick('Get Started', 'Professional', currentSubscriptionPrice)}
                >
                  Get Started
                </button>
                <p className="text-center text-sm text-gray-400 mt-4">14-day free trial</p>
              </div>
            </div>

            {/* Enterprise Tier */}
            <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden hover:border-pink/50 transition duration-300">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold text-white">Enterprise</h3>
                  <span className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full">CUSTOM</span>
                </div>
                <div className="flex items-baseline mb-4">
                  <span className="text-2xl font-bold text-white">Custom pricing</span>
                </div>
                <ul className="text-gray-300 mb-8 space-y-3">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Unlimited AI respondents
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Custom integrations
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Dedicated account manager
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    SLA & premium support
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-pink mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Custom AI model training
                  </li>
                </ul>
                <button 
                  className="w-full border border-gray-600 hover:border-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 hover:bg-blue-500/20"
                  onClick={() => trackButtonClick('Contact Sales', 'Enterprise')}
                >
                  Contact Sales
                </button>
                <p className="text-center text-sm text-gray-400 mt-4">Custom onboarding</p>
              </div>
            </div>
          </div>
        </div>

        {/* Separator line */}
        <div className="max-w-5xl mx-auto mb-16 mt-0">
          <div className="h-px bg-gray-800 w-full"></div>
        </div>

        {/* Section: Pay As You Go */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold mb-4">
              <span className="bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent inline-flex items-center">
                Only {formatDollars(perRespondent)} per AI respondent!
                <img 
                  src="/img/brain-explode.png" 
                  alt="Mind blown emoji" 
                  className="inline-block ml-2 h-10 w-auto"
                />
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Pay only for what you use with our simple pay-per-response model

            </p>
          </div>
          
          {/* Slider Section */}
          <div className="max-w-4xl mx-auto mb-8 px-4">
            <h3 className="text-xl font-medium mb-6 text-center text-white">
              How many respondents do you need?
            </h3>
            <div className="mb-4">
              <input 
                type="range" 
                min="100" 
                max="5000" 
                step="100" 
                value={respondentsCount} 
                onChange={handleSliderChange}
                className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-pink"
                style={{
                  background: `linear-gradient(to right, #f6418f 0%, #f6418f ${calculateSliderPercentage(respondentsCount, 100, 5000)}%, #374151 ${calculateSliderPercentage(respondentsCount, 100, 5000)}%, #374151 100%)`
                }}
              />
            </div>
            
            <div className="text-center">
              <span className="text-xl font-medium bg-gray-900/70 backdrop-blur-sm border border-gray-800 inline-block py-2 px-6 rounded-full text-white">
                {respondentsCount.toLocaleString()} respondents
              </span>
            </div>
          </div>
          
          {/* AI vs Human Pricing Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
            {/* AI Respondents Card - More Prominent */}
            <div className="bg-black/40 backdrop-blur-sm border-2 border-pink rounded-xl overflow-hidden shadow-lg shadow-pink/20 transform md:scale-105 z-10">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-pink to-blue text-white text-xs py-1 px-3 rounded-bl-lg">
                RECOMMENDED
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-3 flex items-center">
                  AI Respondents
                  <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">SAVE {savingsPercentage}%</span>
                </h3>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-6xl font-bold bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent">
                    {formatCurrency(totalAiPrice)}
                  </span>
                </div>
                <p className="text-gray-400 mb-4">
                  Total for {respondentsCount.toLocaleString()} respondents
                </p>
                
                <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg mb-6">
                  <p className="text-green-400 font-medium text-center">
                    Save {formatCurrency(savings)} compared to human respondents
                  </p>
                </div>
                
                <div className="text-2xl font-medium text-white mb-4">
                  {formatDollars(perRespondent)} / respondent
                </div>
                
                <button 
                  className="w-full bg-gradient-to-r from-pink to-blue text-white font-bold py-3 px-6 rounded-lg mb-4 hover:opacity-90 transition-all duration-300 btn-hover"
                  onClick={() => trackButtonClick('Get Started', 'Pay As You Go - AI', totalAiPrice)}
                >
                  Get started
                </button>
                
                <ul className="text-gray-300 space-y-2 mt-6">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-pink mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Fast results (within minutes)</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-pink mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Demographically representative</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-pink mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Unlimited demographic slicing</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Human Respondents Card - Less Prominent */}
            <div className="bg-black/40 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-200 mb-3">Human Respondents</h3>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-5xl font-bold text-white">
                    {formatCurrency(humanTotalPrice)}
                  </span>
                </div>
                <p className="text-gray-400 mb-4">
                  Total for {respondentsCount.toLocaleString()} respondents
                </p>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg mb-6">
                  <p className="text-gray-400 font-medium text-center">
                    Standard market rate for panel respondents
                  </p>
                </div>
                
                <div className="text-xl font-medium text-gray-300 mb-4">
                  {formatDollars(humanPerRespondent)} / respondent
                </div>
                
                <button 
                  className="w-full border border-gray-600 text-gray-300 font-bold py-3 px-6 rounded-lg mb-4 hover:bg-gray-800 transition-colors duration-300"
                  onClick={() => trackButtonClick('Contact Sales', 'Pay As You Go - Human', humanTotalPrice)}
                >
                  Contact sales
                </button>
                
                <ul className="text-gray-400 space-y-2 mt-6">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Slower results (days to weeks)</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Limited demographic availability</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-gray-500 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Additional fees for demographic targeting</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto border-t border-gray-800 pt-16">
          <h2 className="text-2xl font-bold mb-6 text-center bg-gradient-to-r from-pink to-blue bg-clip-text text-transparent">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-pink/50 transition duration-300">
              <h3 className="font-bold text-lg mb-2 text-white">What are AI respondent credits?</h3>
              <p className="text-gray-300">AI respondent credits allow you to generate synthetic survey responses using our advanced AI models. Each credit equals one respondent of AI-generated survey data.</p>
            </div>
            
            <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-pink/50 transition duration-300">
              <h3 className="font-bold text-lg mb-2 text-white">Do credits expire?</h3>
              <p className="text-gray-300">Credits are valid for one year from the date of purchase.</p>
            </div>
            
            <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-pink/50 transition duration-300">
              <h3 className="font-bold text-lg mb-2 text-white">Is there a minimum purchase requirement?</h3>
              <p className="text-gray-300">No, you can purchase as few as 100 AI respondent credits at a time. Volume discounts are available for larger purchases.</p>
            </div>
            
            <div className="bg-black/40 backdrop-blur-sm border border-gray-800 rounded-xl p-6 hover:border-pink/50 transition duration-300">
              <h3 className="font-bold text-lg mb-2 text-white">How do AI respondents compare to human respondents?</h3>
              <p className="text-gray-300">
                AI respondents simulate human survey behavior with remarkable realism at a fraction of the cost. They enable much faster insights and unlimited demographic analysis without the traditional constraints of human panels. 
                While human respondents provide ground truth, AI respondents enable rapid iteration and theory development. <Link to="/blog" className="underline text-white hover:text-pink/70">Read our latest research on the blog</Link> to see how they compare in our testing and real-world studies.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;